import { useEffect, useRef } from 'react';
import { toast } from '@sentinel/ui';
import { FORBIDDEN_EVENT, type ForbiddenEventDetail } from '@sentinel/api-client';

/**
 * Milliseconds during which a repeat of the same backend `error` string is
 * swallowed. 4000 is sonner's TOAST_LIFETIME and neither app's `<Toaster />`
 * overrides `duration`, so anything suppressed here would have been a visual
 * duplicate of a toast still on screen.
 */
const DEDUPE_WINDOW_MS = 4000;

export interface ForbiddenToastListenerProps {
  /** Already-localized copy. `@sentinel/ui` and this package take resolved strings, never `t`. */
  message: string;
}

/**
 * Rendering half of the global permission-denied handler: turns the
 * `sentinel:forbidden` event dispatched by `createForbiddenMiddleware`
 * (`@sentinel/api-client`) into an error toast. Renders nothing itself; mount
 * it once per app, inside the toaster's tree.
 */
export function ForbiddenToastListener({ message }: ForbiddenToastListenerProps): null {
  // Per-instance, not module scope, so a remount starts with a clean window and
  // two mounted apps in one test file cannot suppress each other.
  const last = useRef<{ key: string | undefined; at: number }>({ key: undefined, at: -Infinity });

  useEffect(() => {
    function onForbidden(event: Event) {
      const detail = (event as CustomEvent<ForbiddenEventDetail | undefined>).detail;
      // detail.message is the backend's raw `error` string. It is the dedupe key
      // and nothing else — we never render it, because those are English-only
      // strings like "no org context" that would read badly in a zh-TW UI. This
      // read is not dead code; do not delete it.
      const key = detail?.message;
      const now = Date.now();
      if (key === last.current.key && now - last.current.at < DEDUPE_WINDOW_MS) return;
      last.current = { key, at: now };
      toast.error(message);
    }
    window.addEventListener(FORBIDDEN_EVENT, onForbidden);
    return () => window.removeEventListener(FORBIDDEN_EVENT, onForbidden);
  }, [message]);

  return null;
}
